/**
 * Unified map style configuration for all map components
 *
 * This module provides semantic map types (streets, satellite, neutral) that
 * automatically adapt to light/dark mode based on the global ColorModeProvider.
 */

/**
 * Semantic map types available for both InteractiveMap and StaticMap
 * These are map TYPES, not themes - the actual light/dark theme is determined
 * automatically by the ColorModeProvider.
 */
export const MAP_TYPES = {
  streets: "streets",
  satellite: "satellite",
  neutral: "neutral",
} as const;

/**
 * Map type - represents the visual style of the map (roads vs satellite vs minimal)
 */
export type MapType = keyof typeof MAP_TYPES;

/**
 * Default map type used when none is specified
 */
export const DEFAULT_MAP_TYPE: MapType = "streets";

/**
 * Get the appropriate Mapbox style URL based on map type and color mode
 *
 * This is the single source of truth for map styling across all map components.
 * Both InteractiveMap and StaticMap use this function.
 *
 * @param mapType - The semantic map type (streets, satellite, neutral)
 * @param isDarkMode - Whether dark mode is active (from ColorModeProvider)
 * @param styleOverride - Optional per-organization style URL. When present it
 *   wins over every built-in style: an org whose geography is not the real world
 *   (e.g. a demo environment on invented geography) must not fall back to a
 *   real-world basemap for some map types and not others. Callers usually supply
 *   this via MapStyleProvider rather than per call site.
 * @returns Mapbox style URL
 */
export function getMapStyle(mapType: MapType, isDarkMode: boolean, styleOverride?: string | null): string {
  if (styleOverride) return styleOverride;

  switch (mapType) {
    case "streets":
      return isDarkMode
        ? "mapbox://styles/victor-texture/clzwxtrng005s01p93rtr04op" // Custom dark streets
        : "mapbox://styles/mapbox/streets-v12"; // Default light streets

    case "satellite":
      // Satellite imagery works in both light and dark mode
      return "mapbox://styles/mapbox/satellite-streets-v12";

    case "neutral":
      return isDarkMode
        ? "mapbox://styles/mapbox/dark-v11" // Mapbox dark
        : "mapbox://styles/mapbox/light-v11"; // Mapbox light

    default:
      // Fallback to streets
      return isDarkMode
        ? "mapbox://styles/victor-texture/clzwxtrng005s01p93rtr04op"
        : "mapbox://styles/mapbox/streets-v12";
  }
}
